import { findAtDepthZero, findMatchingCloser, findDocSummary } from './CsTokenScanHelpers';
import { mapVisibility } from './CsVisibilityMapper';

const MODIFIER_KEYWORDS = new Set(['public', 'private', 'protected', 'internal', 'static', 'virtual', 'override', 'abstract', 'readonly', 'async', 'const', 'new', 'sealed', 'partial', 'extern', 'unsafe', 'volatile']);
const VISIBILITY_KEYWORDS = new Set(['public', 'private', 'protected', 'internal']);
const PARAM_MODIFIER_KEYWORDS = new Set(['ref', 'out', 'in', 'params']);

function tokensToText(tokens) {
  return tokens.map(function toValue(t) { return t.value; }).join('').replace(/,/g, ', ');
}

// Collects one member declaration starting at startIndex: leading modifiers,
// then the LAST identifier seen at bracket-depth 0 before a terminator
// ( { ; = ) is the member's name — everything identifier-like before it
// (including generic type args at depth>0) is the type. This works even for
// generic method names ("List<T> Group<T>(...)": the return type's generic
// opens/closes depth back to 0 before "Group", which itself opens its own
// <T> and closes back to 0 right before "(").
function collectDeclaration(bodyTokens, startIndex) {
  let i = startIndex;
  let visibility = null;
  while (bodyTokens[i] && bodyTokens[i].type === 'identifier' && MODIFIER_KEYWORDS.has(bodyTokens[i].value)) {
    if (VISIBILITY_KEYWORDS.has(bodyTokens[i].value)) {
      visibility = bodyTokens[i].value;
    }
    i += 1;
  }

  const declStart = i;
  let depth = 0;
  let lastIdentifierIndex = -1;
  let terminatorIndex = -1;
  for (; i < bodyTokens.length; i += 1) {
    const t = bodyTokens[i];
    if (depth === 0 && (t.value === '(' || t.value === '{' || t.value === ';' || t.value === '=')) {
      terminatorIndex = i;
      break;
    }
    if (t.value === '<' || t.value === '[') {
      depth += 1;
    } else if (t.value === '>' || t.value === ']') {
      depth -= 1;
    } else if (depth === 0 && t.type === 'identifier') {
      lastIdentifierIndex = i;
    }
  }

  if (terminatorIndex === -1 || lastIdentifierIndex === -1) {
    return null;
  }

  return {
    visibility,
    typeTokens: bodyTokens.slice(declStart, lastIdentifierIndex),
    name: bodyTokens[lastIdentifierIndex].value,
    terminatorIndex,
    terminatorValue: bodyTokens[terminatorIndex].value,
  };
}

// Splits a parameter-list token slice on top-level commas (comma-depth-aware,
// so commas inside <...>/(...)/[...] don't split a parameter in half), then
// extracts name/type per parameter. A parameter whose type starts with '('
// (tuple syntax, e.g. "(int, string) x") is flagged unsupported rather than
// mis-parsed.
function parseParameterList(paramTokens) {
  const pieces = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i <= paramTokens.length; i += 1) {
    const isEnd = i === paramTokens.length;
    const value = isEnd ? null : paramTokens[i].value;
    if (!isEnd && (value === '<' || value === '(' || value === '[')) depth += 1;
    if (!isEnd && (value === '>' || value === ')' || value === ']')) depth -= 1;
    if (isEnd || (depth === 0 && value === ',')) {
      if (i > start) {
        pieces.push(paramTokens.slice(start, i));
      }
      start = i + 1;
    }
  }

  const params = [];
  pieces.forEach(function parsePiece(piece) {
    let p = 0;
    while (piece[p] && piece[p].type === 'identifier' && PARAM_MODIFIER_KEYWORDS.has(piece[p].value)) {
      p += 1;
    }
    if (piece[p] && piece[p].value === '(') {
      params.push({ unsupported: true, reason: 'Tuple parameter type not supported' });
      return;
    }
    let depth2 = 0;
    let lastIdent = -1;
    for (let k = p; k < piece.length; k += 1) {
      const t = piece[k];
      if (depth2 === 0 && t.value === '=') break; // default value, ignore for the type/name we care about
      if (t.value === '<' || t.value === '[') depth2 += 1;
      else if (t.value === '>' || t.value === ']') depth2 -= 1;
      else if (depth2 === 0 && t.type === 'identifier') lastIdent = k;
    }
    if (lastIdent === -1) {
      return;
    }
    params.push({ name: piece[lastIdent].value, type: tokensToText(piece.slice(p, lastIdent)) });
  });

  return params;
}

/**
 * Scans a class/interface body's tokens for top-level members (properties,
 * fields, methods) and constructors, including full parameter lists.
 * Constructors (name === className) are included as memberKind: 'constructor'
 * with visibility taken from their own modifier (e.g. public/protected ctor) —
 * no return type in their signature. Members using an unsupported construct
 * (a tuple parameter type) are skipped with a reason instead of mis-parsed.
 * @param {{type: string, value: string}[]} bodyTokens
 * @param {string} className - used to detect constructors
 * @param {{tokenIndex: number, summary: string}[]} docComments - from tokenizeCs, full-token-array indices
 * @param {number} bodyOffset - index of bodyTokens[0] in the full token array, to translate local i -> docComments lookup
 * @returns {{members: {visibility: string, text: string, memberKind: 'field'|'method'|'constructor', description: string|null, dependencies: {name: string, type: string}[]|undefined}[], skipped: {name: string, reason: string}[]}}
 */
export function scanCsMembers(bodyTokens, className, docComments = [], bodyOffset = 0) {
  const members = [];
  const skipped = [];
  let i = 0;

  while (i < bodyTokens.length) {
    const decl = collectDeclaration(bodyTokens, i);
    if (!decl) {
      break;
    }

    const description = findDocSummary(docComments, bodyOffset + i);
    const visibility = mapVisibility(decl.visibility || 'public');
    const typeText = tokensToText(decl.typeTokens);

    if (decl.terminatorValue === '(') {
      const parenClose = findMatchingCloser(bodyTokens, decl.terminatorIndex);
      const params = parseParameterList(bodyTokens.slice(decl.terminatorIndex + 1, parenClose));

      const afterParams = findAtDepthZero(bodyTokens, parenClose + 1, ['{', ';']);
      const hasBody = bodyTokens[afterParams] && bodyTokens[afterParams].value === '{';
      const methodBodyClose = hasBody ? findMatchingCloser(bodyTokens, afterParams) : afterParams;
      const nextIndex = hasBody ? methodBodyClose + 1 : afterParams + 1;
      // Kept for CsSeqDia's call-scanner (method body needed to trace call chains) — ignored by class-diagram consumers.
      const methodBodyTokens = hasBody ? bodyTokens.slice(afterParams + 1, methodBodyClose) : undefined;

      const isConstructor = decl.name === className;

      if (params.some(function isUnsupported(p) { return p.unsupported; })) {
        skipped.push({ name: decl.name, reason: 'Tuple parameter type not supported' });
        i = nextIndex;
        continue;
      }

      const paramText = params.map(function toParamText(p) { return `${p.name}: ${p.type}`; }).join(', ');
      const memberKind = isConstructor ? 'constructor' : 'method';
      const text = isConstructor ? `${decl.name}(${paramText})` : `${decl.name}(${paramText}): ${typeText}`;
      // For constructors, params ARE the class's dependencies (à la Disconnected
      // Domain-style ctors) — keep them as structured data so the tooltip can
      // list them one per line, separate from any real XMLDoc `description`.
      const dependencies = isConstructor ? params.map(function toDependency(p) { return { name: p.name, type: p.type }; }) : undefined;
      // Structured params for EVERY method (ctor included) — CsSeqDia's
      // parameter-toggle (2026-07-19, hotkey P) needs name/type as data, not
      // re-parsed out of `text`. Kept separate from `dependencies` above
      // (same values for a ctor) since that field's naming is ctor-specific.
      const structuredParams = params.map(function toStructuredParam(p) { return { name: p.name, type: p.type }; });
      // returnType as its own field (not just baked into `text`) — CsSeqDia's
      // return-arrow labeling (2026-07-19) needs it structured, not re-parsed
      // out of the display string.
      const returnType = isConstructor ? undefined : typeText;
      members.push({ name: decl.name, visibility, text, memberKind, description, dependencies, params: structuredParams, paramCount: params.length, bodyTokens: methodBodyTokens, returnType });
      i = nextIndex;
      continue;
    }

    if (decl.terminatorValue === '{') {
      const closeIndex = findMatchingCloser(bodyTokens, decl.terminatorIndex);
      members.push({ name: decl.name, visibility, text: `${decl.name}: ${typeText}`, memberKind: 'field', description });
      // Auto-property with a default-value initializer, e.g. `{ get; set; } = 50;`
      // — skip the `= ...;` tail too, otherwise the next collectDeclaration()
      // starts ON the `=` token, finds no identifier before its own terminator,
      // returns null, and silently kills the rest of the scan (see scanCsMembers's
      // `if (!decl) break`).
      let nextIndex = closeIndex + 1;
      if (bodyTokens[nextIndex] && bodyTokens[nextIndex].value === '=') {
        nextIndex = findAtDepthZero(bodyTokens, nextIndex + 1, [';']) + 1;
      }
      i = nextIndex;
      continue;
    }

    if (decl.terminatorValue === '=') {
      const semiIndex = findAtDepthZero(bodyTokens, decl.terminatorIndex + 1, [';']);
      members.push({ name: decl.name, visibility, text: `${decl.name}: ${typeText}`, memberKind: 'field', description });
      i = semiIndex + 1;
      continue;
    }

    members.push({ name: decl.name, visibility, text: `${decl.name}: ${typeText}`, memberKind: 'field', description });
    i = decl.terminatorIndex + 1;
  }

  return { members, skipped };
}
